import { CustomFieldCustomization, LogicFieldType, LogicOperator, VisibilityField, VisibilityOption, VisibilityRule } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { isDefined, waitForElement } from '../helpers/utils';

import { ifDefined } from 'lit/directives/if-defined';
import topicData from '../helpers/state/topic-data';

@customElement('form-visibility-component')
export class FormVisibilityComponent extends LitElement {
    static amplitude_callsite: string = 'Form Visibility Component';

    @query('#ruleTarget')
    ruleTargetElement?: HTMLSelectElement;

    @query('#visibilityOperator')
    visibilityOperatorElement?: HTMLSelectElement | any;

    @query('#visibilityRuleValue')
    visibilityRuleValueElement?: HTMLInputElement;

    @query('#visibilityComparisonValueText')
    visibilityComparisonValueTextElement?: HTMLInputElement;

    @query('#visibilityComparisonValueSelect')
    visibilityComparisonValueSelectElement?: HTMLSelectElement;

    @query('#combineOperator')
    combineOperatorElement?: HTMLSelectElement;

    @state()
    visibilityRules: VisibilityRule[][] = [];

    @state()
    visibilityTargetOptions: VisibilityField[] = [];

    @state()
    customFormElements: CustomFieldCustomization[] = [];

    @state()
    currentFieldValue: string = '';

    // In component properties

    @state()
    operatorOptions: LogicOperator[] = [];

    @state()
    showRuleValue: boolean = false;

    @state()
    showComparisonValueText: boolean = false;

    @state()
    showComparisonValueSelect: boolean = false;

    @state()
    comparisonValueSelectOptions: VisibilityOption[] = [];

    @state()
    selectedVisibilityOption: VisibilityField | undefined;

    @state()
    visibilityTargetRule: VisibilityRule[] | undefined;

    @state()
    visibilityTargetRuleIndex: number | undefined;

    @state()
    visibilityTargetSubRule: VisibilityRule | undefined;

    @state()
    visibilityTargetSubRuleIndex: number | undefined;

    @state()
    loadingRuleFields: boolean = false;

    @state()
    needsSave = false;

    updated(changedProperties: Map<string, any>) {
        if ((changedProperties.has('visibilityRules')  || (changedProperties.has('needsSave')) && this.needsSave)) {
            // send an event to the parent component that the visibility rules have changed
            this.dispatchEvent(new CustomEvent('visibility-rules-updated', {
                detail: {
                    visibilityRules: this.visibilityRules ?? [],
                    fieldName: this.currentFieldValue
                },
                bubbles: true,
                composed: true
            }));
            this.needsSave = false;
        }
    }

    async updateRuleTarget(value: string) {
        this.loadingRuleFields = true;
        if (!this.ruleTargetElement) {
            const elementExists = await waitForElement(this.ruleTargetElement).then(() => true).catch(() => false);
            if (!elementExists) {
                return;
            }
        };
        this.loadingRuleFields = false;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.ruleTargetElement!.value = value;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.ruleTargetElement!.dispatchEvent(new Event('change'));
        this.needsSave = true;
    }

    onUpdateRuleTarget(event: Event) {
        const selectElement = event.target as HTMLSelectElement;
        const selectedValue = selectElement.value;
        if (selectedValue !== this.visibilityTargetSubRule?.targetField && this.visibilityTargetSubRule && this.ruleTargetElement) {
            this.visibilityTargetSubRule.targetField = selectedValue;
            this.ruleTargetElement.value = selectedValue;
        };
        let fieldType: string | undefined;

        // look for value in standardFields and customFields to match the type
        const standardField = this.visibilityTargetOptions.find(field => field.value === selectedValue);
        if (standardField) {
            fieldType = standardField.inputType;
            this.selectedVisibilityOption = this.visibilityTargetOptions.find(field => field.value === selectedValue);
        }

        if (!standardField) {
            const customField = this.visibilityTargetOptions.find(field => field.value === selectedValue);
            if (customField) {
                fieldType = customField.inputType;
                this.selectedVisibilityOption = this.visibilityTargetOptions.find(field => field.value === selectedValue);
            }
        }

        // We can add these kinds of filters in the future.
        // if (selectedValue === 'Role') {
        //     fieldType = 'select';
        // } else if (selectedValue === 'Parameter') {
        //     fieldType = 'parameter';
        // }

        let options;
        if (fieldType === 'select') {
            options = this.visibilityTargetOptions.find(field => field.value === selectedValue.toLowerCase())?.options ??
                this.visibilityTargetOptions.find(field => field.name === selectedValue)?.options;
        } else if (fieldType === 'text') {
            options = undefined;
        }

        this.updateVisibilityOptions(fieldType ?? 'text', selectedValue, options );
        this.needsSave = true;
    }

    getItemNamefromOid(oid: string) {
        return topicData.categories.find(category => category._id?.$oid === oid)?.name
            ?? topicData.allUserTags.find(tag => tag._id?.$oid === oid)?.name
            ?? this.customFormElements.find(customField => customField._id?.$oid === oid)?.customizations.header
            ?? oid;
    }

    updateVisibilityOptions(type: string, formPart: string, options?: VisibilityOption[]) {
        switch (type) {
            case 'select':
                this.showComparisonValueSelect = true;
                this.showComparisonValueText = false;
                this.comparisonValueSelectOptions = options ?? [];
                if (formPart === 'category') {
                    // different because this is mandatory and single selection
                    this.operatorOptions = [LogicOperator.Equals, LogicOperator.NotEquals];
                } else {
                    this.operatorOptions = [LogicOperator.Equals, LogicOperator.NotEquals, LogicOperator.IsSet, LogicOperator.IsNotSet, LogicOperator.Contains, LogicOperator.NotContains];
                }
                break;
            case 'number':
                this.showComparisonValueSelect = false;
                this.showComparisonValueText = true;
                this.operatorOptions = [LogicOperator.GreaterThan, LogicOperator.LessThan, LogicOperator.Equals, LogicOperator.NotEquals, LogicOperator.IsSet, LogicOperator.IsNotSet];
                break;
            case 'text':
                this.showComparisonValueSelect = false;
                this.showComparisonValueText = true;
                this.operatorOptions = [LogicOperator.Contains, LogicOperator.NotContains, LogicOperator.Equals, LogicOperator.NotEquals, LogicOperator.IsSet, LogicOperator.IsNotSet];
                break;
            default:
                this.operatorOptions = [LogicOperator.IsSet, LogicOperator.IsNotSet];
        }
        this.requestUpdate();
    }

    updateOperator(event: Event) {
        const selectElement = event.target as HTMLSelectElement;
        const selectedValue = selectElement.value;

        if (selectedValue === LogicOperator.IsSet || selectedValue === LogicOperator.IsNotSet) {
            this.showComparisonValueText = false;
            this.showComparisonValueSelect = false;
        }  else if (this.selectedVisibilityOption?.inputType === 'select' && (selectedValue === LogicOperator.Contains || selectedValue === LogicOperator.NotContains)) {
            this.showComparisonValueText = true;
            this.showComparisonValueSelect = false;
        } else if (this.selectedVisibilityOption?.inputType === 'select') {
            this.showComparisonValueText = false;
            this.showComparisonValueSelect = true;
        } else {
            this.showComparisonValueText = true;
            this.showComparisonValueSelect = false;
        }

        this.requestUpdate();
    }

    resetLogicEditor() {
        this.visibilityTargetRule = undefined;
        this.visibilityTargetRuleIndex = undefined;
        this.visibilityTargetSubRule = undefined;
        this.visibilityTargetSubRuleIndex = undefined;
        this.requestUpdate();
    }

    addOrUpdateAndRulet() {
        if (!this.visibilityTargetRule || this.visibilityTargetRule.length === 0 || this.visibilityTargetRuleIndex === undefined) {
            return;
        }
        this.visibilityRules = this.visibilityRules ?? [];
        this.visibilityRules?.splice(this.visibilityTargetRuleIndex ?? 0, 1, this.visibilityTargetRule);
        this.needsSave = true;
        this.resetLogicEditor();
    }

    cancelRuleEdit() {
        // remove the current rule if there are no subrules
        if (this.visibilityTargetRule && this.visibilityTargetRule.length === 0) {
            this.visibilityRules?.splice(this.visibilityTargetRuleIndex ?? 0, 1);
        }
        this.resetLogicEditor();
    }

    async addOrRule(rule_index: number) {
        this.visibilityTargetRule = this.visibilityRules[rule_index] ?? [];

        this.visibilityTargetRule.push({
            targetField: this.ruleTargetElement?.value ?? '',
            comparisonValue: this.visibilityComparisonValueTextElement?.value ?? this.visibilityComparisonValueSelectElement?.value ?? '',
            extendedComparisonString: this.visibilityRuleValueElement?.value ?? '',
            fieldType: this.selectedVisibilityOption?.formPart ?? LogicFieldType.StandardField,
            operator: this.visibilityOperatorElement?.value ?? this.operatorOptions
        });
        // set this new rule as the visibilityTargetSubrule
        await this.setSubRuleEditable(rule_index, this.visibilityTargetRule.length - 1);
        await this.updateRuleTarget('category');
    }

    save() {
        // dispatch the updated visibility rules to the parent component
        this.dispatchEvent(new CustomEvent('visibility-rules-updated', {
            detail: {
                visibilityRules: this.visibilityRules ?? [],
                fieldName: this.currentFieldValue
            },
            bubbles: true,
            composed: true,
        }));
        this.needsSave = false;
    }


    async updateSubRule() {
        if (!this.visibilityTargetRule || !this.visibilityTargetSubRule || this.visibilityTargetSubRuleIndex === undefined) {
            return;
        }
        const newSubRule: VisibilityRule = {
            targetField: this.ruleTargetElement?.value ?? '',
            comparisonValue: this.visibilityComparisonValueTextElement?.value ?? this.visibilityComparisonValueSelectElement?.value ?? '',
            extendedComparisonString: this.visibilityRuleValueElement?.value ?? '',
            fieldType: this.selectedVisibilityOption?.formPart ?? LogicFieldType.StandardField,
            operator: this.visibilityOperatorElement?.value ?? '',
        };
        // fund this.visibilityTargetSubRule in the visibilityTargetRule and replace it with the new subrule
        this.visibilityTargetRule?.splice(this.visibilityTargetSubRuleIndex, 1, newSubRule);

        this.resetLogicEditor();
        await this.save();
    }

    cancelSubRuleEdit() {
        // delete any subrules in this rule that are empty
        if (this.visibilityTargetRule) {
            this.visibilityTargetRule = this.visibilityTargetRule.filter(subRule => subRule.targetField !== '' && subRule.operator !== undefined);
            // delete this rule if the array is empty
            if (this.visibilityTargetRule.length === 0 ) {
                this.visibilityRules?.splice(this.visibilityTargetRuleIndex ?? 0, 1);
            }
        }
        this.resetLogicEditor();
    }

    async setSubRuleEditable(ruleIndex: number, subRuleIndex: number) {

        this.visibilityTargetRule = this.visibilityRules?.[ruleIndex];
        const new_rule_index = ruleIndex.valueOf();
        this.visibilityTargetRuleIndex = new_rule_index;

        this.visibilityTargetSubRule = this.visibilityTargetRule?.[subRuleIndex];
        const new_subrule_index = subRuleIndex.valueOf();
        this.visibilityTargetSubRuleIndex = new_subrule_index;

        this.requestUpdate();
        await this.updateRuleTarget(this.visibilityTargetSubRule?.targetField ?? 'category');
        this.visibilityOperatorElement.value = this.visibilityTargetSubRule?.operator ?? '';
        this.visibilityOperatorElement.dispatchEvent(new Event('change'));
    }

    async addAndRule() {
        const newSubRule: VisibilityRule = {
            targetField: this.ruleTargetElement?.value ?? 'category',
            comparisonValue: this.visibilityComparisonValueTextElement?.value ?? this.visibilityComparisonValueSelectElement?.value ?? '',
            extendedComparisonString: this.visibilityRuleValueElement?.value ?? '',
            fieldType: this.selectedVisibilityOption?.formPart ?? LogicFieldType.StandardField,
            operator: this.visibilityOperatorElement?.value ?? LogicOperator.Equals,
        };
        this.visibilityRules?.push([newSubRule]);
        this.visibilityTargetRule = this.visibilityRules?.[this.visibilityRules.length - 1];
        this.visibilityTargetSubRule = this.visibilityTargetRule[0];
        this.visibilityTargetRuleIndex = this.visibilityRules.length - 1;
        this.visibilityTargetSubRuleIndex = 0;

        this.requestUpdate();
        await this.updateRuleTarget('category');
    }

    renderSubRuleEditor() {
        return html`
            ${this.loadingRuleFields ? html`
                <loading-element
                    loadingString="Loading Field Options"
                    ?horizontal=${true}
                ></loading-element>
            ` : ''}
            <div class="ruleSelectorWrapper visibilityRules ${this.loadingRuleFields ? 'hidden' : ''}">
                <div>
                    <select id="ruleTarget" @change=${this.onUpdateRuleTarget}>
                        <optgroup label="Standard Fields">
                            ${this.visibilityTargetOptions.filter(field => field.formPart === LogicFieldType.StandardField && field.value !== this.currentFieldValue).map(field => html`
                                <option value="${field.value}">${field.name}</option>
                            `)}
                        </optgroup>
                        <optgroup label="Custom Fields">
                            ${this.visibilityTargetOptions.filter(field => field.formPart === LogicFieldType.CustomField && field.value !== this.currentFieldValue).map(field => html`
                                <option value="${field.value}">${field.name}</option>
                            `)}
                        </optgroup>
                        <!-- <optgroup label="Other">
                            <option value="Role">Role</option>
                            <option value="Parameter">Parameter</option>
                        </optgroup> -->
                    </select>
                </div>
                ${this.showRuleValue ? html`
                    <div>
                        <input id="visibilityRuleValue" type="text" value=${ifDefined(this.visibilityTargetSubRule?.extendedComparisonString)} />
                    </div>
                ` : ''}
                <div>
                    <select id="visibilityOperator" @change=${this.updateOperator} value=${this.visibilityTargetSubRule?.operator ?? 'Select a Rule'}>
                        ${this.operatorOptions.map(operator => html`
                            <option .value="${operator}">${operator}</option>
                        `)}
                    </select>
                </div>
                ${this.showComparisonValueText ? html`
                    <div>
                        <input id="visibilityComparisonValueText" type="text" value=${this.visibilityTargetSubRule?.comparisonValue ?? 'Select a Rule'} />
                    </div>
                ` : this.showComparisonValueSelect ? html`
                    <div>
                        <select id="visibilityComparisonValueSelect" value=${ifDefined(this.visibilityTargetSubRule?.comparisonValue)}>
                            ${this.comparisonValueSelectOptions.map(option => html`
                                <option value="${option.value}">${option.name}</option>
                            `)}
                        </select>
                    </div>
                ` : ''}
            </div>
            ${!this.loadingRuleFields ? html`
                <div class="ruleSubmissionButtons">
                    <button @click=${() => this.cancelSubRuleEdit()}>
                        Cancel
                    </button>
                    <button @click=${async() => await this.updateSubRule()}>
                        Update
                    </button>
                </div>
            ` : ''}
        `;
    }

    render() {
        return html`
            <div class="existingRules">
                ${this.visibilityRules.map((rule, index) => {
                    const rule_index = index.valueOf();
                    return html`
                        <div class="rule">
                            ${rule.map((subRule, index) => {
                                const targetFieldName = this.getItemNamefromOid(subRule.targetField);
                                const targetComparisonValue = this.getItemNamefromOid(subRule.comparisonValue);
                                const subRule_index = index.valueOf();
                                const editing_this_rule = this.visibilityTargetRuleIndex === rule_index && this.visibilityTargetSubRuleIndex === subRule_index;
                                const editing_another_rule = !editing_this_rule && (this.visibilityTargetRuleIndex !== undefined || this.visibilityTargetSubRuleIndex !== undefined);
                                if (editing_this_rule) {
                                    return html `
                                        ${this.renderSubRuleEditor()}
                                        ${index < rule.length - 1 ? html`<div class="operator-holder"><span class="operator or">OR</span></div>` : ''}
                                    `;
                                } else {
                                    return html`
                                        <span class="ruleName">
                                            ${!editing_another_rule ? html`
                                                <mwc-icon @click=${async () => {
                                                    await this.setSubRuleEditable(rule_index, subRule_index);
                                                }
                                                }>edit</mwc-icon>
                                            ` : ''}
                                            ${targetFieldName} <b>${subRule.operator}</b> ${targetComparisonValue}
                                            ${!editing_another_rule ? html`
                                                <mwc-icon @click=${() => {
                                                    this.visibilityRules?.[rule_index].splice(subRule_index, 1);
                                                    if (this.visibilityRules?.[rule_index].length === 0) {
                                                        this.visibilityRules?.splice(rule_index, 1);
                                                    }
                                                    this.requestUpdate();
                                                }}>delete</mwc-icon>
                                            ` : ''}
                                        </span>
                                        ${subRule_index < rule.length - 1 ?
                                            html`<div class="operator-holder"><span class="operator or">OR</span></div>` :
                                            !editing_another_rule ?
                                                html`<div class="operator-holder" @click=${async () => await this.addOrRule(rule_index)}><span class="operator or clickable-or"><mwc-icon>add_circle</mwc-icon> OR</span></div>`
                                                : ''
                                        }
                                    `;
                                }
                            })}
                        </div>
                        ${isDefined(this.visibilityRules) && index < this.visibilityRules.length - 1  ? html`<div class="operator-holder"><span class="operator and">AND</span></div>` : ''}
                    `;
                })}
            </div>
        `;
    }


    static get styles() {
        return css`

            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
                flex-wrap: wrap;
            }

            .checkboxHolder {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .right-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
                flex-wrap: wrap;
            }
            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden auto;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .general-stats-area {
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 1;
            }

            h4.sub-title {
                font-weight: 600;
                font-size: 12px;
                line-height: 100%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            #holder {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            #container {
                flex-direction: column;
                overflow: hidden auto;
                padding-right: 20px;
                box-sizing: border-box;
                flex: 1;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
                margin-top: 5px;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .green {
                --mdc-theme-primary: var(--main-success-color);
            }

            .gray {
                --mdc-theme-primary: var(--main-disabled-color);
            }

            .save-actions {
                display: flex;
                justify-content: flex-end;
                margin: 0.5rem;
            }

            .visibility-square {
                display: flex;
                margin-left: 0.5rem;
                color: var(--theme-input-color);
                border: 1px solid var(--theme-input-color);
                border-radius: 5px;
                padding: 0.1rem 0.25rem;
                align-items: center;
                font-size: 0.8rem;
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }

            .individual-participant-drawer {
                border-right: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
                background-color: var(--theme-white-color);
                padding: 1rem;
                transition: width 0.3s;
                display: flex;
                flex-direction: column;
                display: flex;
                overflow-y: auto;
            }

            .individual-participant-drawer.open {
                width: 30%;
            }

            .individual-participant-drawer.closed {
                width: 0;
                padding: 0;
            }

            .helpSubHeader {
                display: inline-flex;
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
                align-items: center;
            }

            .summary {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .summary-section {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
                pointer-events: none;
            }

            .move {
                margin-left: 1rem;
            }

            .summary mwc-icon {
                margin-right: 0.5rem;
                --mdc-icon-size: 20px;
            }

            .move-icon {
                cursor: pointer;
                margin: 0;
            }

            .move-icon:hover {
                color: var(--main-primary-color);
            }

            .existingRules {
                display: flex;
                flex-direction: column;
                --mdc-icon-size: 16px;
            }

            .existingRules mwc-icon {
                cursor: pointer;
                margin-right: 0.5rem;
                margin-left: 0.5rem;
            }

            .ruleName {
                margin: 0;
                font-size: 0.8rem;
                text-align: center;
                flex: 1;
                border: 1px solid var(--main-secondary-color);
                display: flex;
                align-items: center;
                gap: 5px;
            }

            .rule {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid var(--main-primary-color);
                padding: 0.5rem;
            }

            .operator-holder {
                display: flex;
                justify-content: center;
                flex-direction:row;

            }

            .operator {
                text-align: center;
                font-size: 0.6rem;
                font-weight: bold;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
                color: white;
                border-radius: 5px;
                padding: 0 0.5rem;
                --mdc-icon-size: 12px;
            }

            .operator mwc-icon {
                margin-left: 0;
                margin-right: 0.25rem;

            }

            .and {
                background-color: var(--main-primary-color);
                color : white;
            }

            .or {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-or {
                cursor: pointer;
                color: var(--main-secondary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-or:hover {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-and {
                cursor: pointer;
                color: var(--main-primary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-and:hover {
                background-color: var(--main-primary-color);
                color: white;
            }

            .visibilityRules {
                display: flex;
                flex-direction: column;
                margin-top: 1rem;
            }

            .helpSubeHeaderUnderline {
                margin: 0px;
                font-weight: normal;
                font-size: 0.8rem;
                flex: 1;
                text-decoration: underline;
                color: var(--theme-primary-color);
                display: inline-flex;
            }

            form-tooltip {
                font-size: 0.8rem;
            }

            .archiveField {
                cursor: pointer;
                display: flex;
                align-items: flex-end;
                justify-content: flex-end;
                text-decoration: underline;
                color: var(--main-invalid-color);
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .input {
                width: 100%;
                padding: auto 5px;
                box-sizing: border-box;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }

            .ruleSelectorWrapper {
                display: flex;
                margin-top: 1rem;
                flex-direction: row;
                flex-wrap: wrap;
                align-content: center;
                justify-content: center;
                gap: 3px 5px;
            }

            .gatedSection {
                cursor: pointer;
            }

            .description {
                color: var(--theme-input-color);
            }

            .add-new-rule-wrapper {
                width: 100%;
                display: flex;
                justify-content: center;
                padding-top: 5px;
            }

            .customizedFields__disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .hidden {
                display: none;
            }

            .ruleSubmissionButtons {
                display: flex;
                justify-content: center;
                margin-top: 5px;
                padding-bottom: 10px;
                gap: 5px;
            }

            .payment-options {
                margin-top: 1rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .input__payment-amount,
            .input__donation-amount,
            .input__payment-message {
                width: 100%;
                margin-top: 0.5rem;
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }
            }
        `;
    }
}
